/**
 * Development mode utilities for bypassing authentication and selected API calls.
 *
 * **Opt-in only:** set `VITE_LOCAL_FULL_MOCK=true` in a gitignored file such as
 * `.env.local` (see `.env.example`). Never enable in production builds.
 * Can be explicitly disabled with `?dev=false` in the URL when the env flag is on.
 */

import type { User } from '../services/types'

export const isDevMode = (): boolean => {
  // Always disabled in production builds (safety check)
  if (import.meta.env.PROD) return false;

  // Opt-in: mock auth + in-memory policy/user APIs only when explicitly enabled locally
  // (e.g. `.env.local`, which is gitignored.) Vitest sets the same key via `vitest.config.ts` `env`.
  if (import.meta.env.VITE_LOCAL_FULL_MOCK !== 'true') return false;

  if (!import.meta.env.DEV) return false;

  const urlParams = new URLSearchParams(window.location.search);
  const devParam = urlParams.get('dev');

  if (devParam === 'false') return false;

  return true;
};

/**
 * Mock account for dev mode
 */
export const getMockAccount = () => ({
  homeAccountId: 'dev-account-id',
  environment: 'dev',
  tenantId: 'dev-tenant-id',
  username: 'dev@test.com',
  localAccountId: 'dev-local-account-id',
  name: 'Dev User',
});

/**
 * Comprehensive mock policy data for dev mode
 * Includes all fields used by PolicyView and other components
 */
const mockPoliciesData: Record<string, ReturnType<typeof createMockPolicy>> = {};

/** Map Entsia `typeOfLicence` codes to portal listed-driver labels (aligned with entsia payload extractor). */
function portalLicenseTypeFromEntsiaCode(code: string | undefined): string {
  const k = String(code || 'FULL').toUpperCase();
  const map: Record<string, string> = {
    FULL: 'Full Australian Licence',
    P1: "Provisional 1 (P1) Licence",
    P2: "Provisional 2 (P2) Licence",
    PROVISIONAL: "Provisional 1 (P1) Licence",
    LEARNER: "Learner's Licence",
    INTERNATIONAL: 'International Licence',
  };
  return map[k] || 'Full Australian Licence';
}

function createMockPolicy(policyRef: string, overrides: Record<string, any> = {}) {
  const defaults: Record<string, Record<string, any>> = {
    'dxt472': {
      registrationNumber: 'ABC 123',
      registrationState: 'NSW',
      vehicleName: '2024 Toyota LandCruiser 300 Series',
      makeAndModel: 'TOYOTA LANDCRUISER 300 SERIES',
      premium: 1847.50,
      basicExcess: 750,
      sumInsured: 125000,
      modificationsValue: 24500,
      mainDriver: 'James Wilson',
      listedDrivers: ['Sarah Wilson', 'Michael Chen'],
      listedDriversDetails: [
        {
          id: '1',
          firstName: 'Sarah',
          lastName: 'Wilson',
          dateOfBirth: '15/03/1985',
          gender: 'Female',
          licenseType: 'Full Australian Licence',
          licenseAge: 18,
          hasSafetyCourse: true,
          claims: []
        },
        {
          id: '2',
          firstName: 'Michael',
          lastName: 'Chen',
          dateOfBirth: '22/07/1990',
          gender: 'Male',
          licenseType: 'Full Australian Licence',
          licenseAge: 19,
          hasSafetyCourse: false,
          claims: [
            {
              id: 'claim-1',
              date: '10/06/2023',
              type: 'Collision',
              driverAtFault: false
            }
          ]
        }
      ],
      parkingAddress: '42 Eucalyptus Drive, Brookvale NSW 2100',
      email: 'james.wilson@example.com',
      mobileNumber: '0412 345 678',
    },
    'POL40000001': {
      registrationNumber: 'XYZ 789',
      registrationState: 'NSW',
      vehicleName: '2023 Ford Ranger Raptor',
      makeAndModel: 'FORD RANGER RAPTOR',
      premium: 245.00,
      basicExcess: 500,
      sumInsured: 85000,
      modificationsValue: 15000,
      mainDriver: 'Emma Thompson',
      listedDrivers: ['David Thompson'],
      listedDriversDetails: [
        {
          id: '1',
          firstName: 'David',
          lastName: 'Thompson',
          dateOfBirth: '08/11/1988',
          gender: 'Male',
          licenseType: 'Full Australian Licence',
          licenseAge: 17,
          hasSafetyCourse: true,
          claims: []
        }
      ],
      parkingAddress: '15 Mountain View Rd, Castle Hill NSW 2154',
      email: 'emma.t@example.com',
      mobileNumber: '0423 456 789',
      autoRenewal: 'Off',
      paymentFrequency: 'MONTHLY',
      premiumFrequency: 'monthly',
      remainingInstalments: 7,
    },
    'POL40000399': {
      registrationNumber: 'XYZ 789',
      registrationState: 'NSW',
      vehicleName: '2024 Ford Ranger',
      makeAndModel: 'FORD RANGER',
      premium: 245.00,
      basicExcess: 500,
      sumInsured: 85000,
      modificationsValue: 15000,
      mainDriver: 'John Smith',
      listedDrivers: ['John Smith'],
      listedDriversDetails: [
        {
          id: '1',
          firstName: 'John',
          lastName: 'Smith',
          dateOfBirth: '01/05/1985',
          gender: 'Male',
          licenseType: 'Full Australian Licence',
          licenseAge: 20,
          hasSafetyCourse: false,
          claims: [],
        },
      ],
      parkingAddress: '10 Test Street, Sydney NSW 2000',
      email: 'john.smith@example.com',
      mobileNumber: '0400 000 000',
      autoRenewal: 'Off',
      paymentFrequency: 'MONTHLY',
      premiumFrequency: 'monthly',
      remainingInstalments: 7,
      instalmentAmount: 350,
    },
  };

  const policyDefaults = defaults[policyRef] || defaults['dxt472'];

  return {
    // Required PolicyLink fields
    policyLinkId: `dev-link-${policyRef}`,
    policyRef,
    productRef: 'club4x4-comprehensive',
    customerId: 'dev-customer-id',
    linkedAt: new Date().toISOString(),

    // Policy identification
    policyNumber: policyRef.toUpperCase(),

    // Vehicle details
    registrationNumber: policyDefaults.registrationNumber,
    ...(policyDefaults.registrationState && { registrationState: policyDefaults.registrationState }),
    vehicleName: policyDefaults.vehicleName,
    makeAndModel: policyDefaults.makeAndModel,
    year: '2024',

    // Cover dates
    coverStartDate: '2025-01-15',
    coverEndDate: '2026-01-15',
    termStartDate: '2025-01-15',
    termEndDate: '2026-01-15',
    renewalDate: '2026-01-15',
    renewalDateFormatted: '15 January 2026',

    // Premium and payment
    premium: policyDefaults.premium,
    premiumFrequency: policyDefaults.premiumFrequency || 'annually',
    paymentFrequency: policyDefaults.paymentFrequency || 'ANNUAL',
    paymentMethodDisplay: 'Credit card',
    paymentVaultKeyExists: 'NO',
    autoRenewal: policyDefaults.autoRenewal || 'On',
    remainingInstalments: policyDefaults.remainingInstalments ?? null,
    ...(typeof (policyDefaults as { instalmentAmount?: number }).instalmentAmount === 'number'
      ? { instalmentAmount: (policyDefaults as { instalmentAmount?: number }).instalmentAmount }
      : {}),

    // Excess and value
    basicExcess: policyDefaults.basicExcess,
    vehicleValue: 'Agreed value',
    sumInsured: policyDefaults.sumInsured,
    modificationsValue: policyDefaults.modificationsValue,

    // Vehicle usage
    parkingAddress: policyDefaults.parkingAddress,
    overnightParking: 'Garage',
    vehicleUse: 'Private',
    vehicleFinance: 'No',

    // Drivers
    mainDriver: policyDefaults.mainDriver,
    listedDrivers: policyDefaults.listedDrivers,
    listedDriversDetails: policyDefaults.listedDriversDetails,

    // Contact details
    policyContact: policyDefaults.mainDriver,
    policyContactEmail: policyDefaults.email,
    policyContactPostalAddress: policyDefaults.parkingAddress,
    authorisedPeople: 'None specified',
    email: policyDefaults.email,
    postalAddress: policyDefaults.parkingAddress,

    // Policy extras
    offRoadRecovery: '$10,000',
    carHire: '$90/day for 14 days',
    excessFreeGlass: 'Yes',
    roadsideAssistance: 'Yes',
    personalEffects: '$2,000',

    // Document links (for ISSUED policies; used by PolicyView Documents section and tests)
    documentLinks: {
      'certificate-of-insurance': true,
      'certificate-of-currency': true,
      'tax-invoice': true,
      pds: true,
    },

    // Override with any custom values
    ...overrides,
  };
}

export const getMockPolicy = (policyRef: string = 'dxt472') => {
  // Cache mock policies for consistency
  if (!mockPoliciesData[policyRef]) {
    mockPoliciesData[policyRef] = createMockPolicy(policyRef);
  }
  return mockPoliciesData[policyRef];
};

/** Display strings for off-road tiers (align with Policy extras labels). */
const OFF_ROAD_DISPLAY_BY_OPTION: Record<string, string> = {
  '1500': 'Up to $1,500',
  '5000': 'Up to $5,000',
  '15000': 'Up to $15,000',
  '30000': 'Up to $30,000',
}

function apiDobToDisplayDob(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const [, y, mo, d] = m
  return `${parseInt(d, 10)}/${parseInt(mo, 10)}/${y}`
}

export type DevMockPolicyUpdateBody = {
  registrationNumber: string
  state?: string
  parkingAddress?: string
  overnightParking?: string
  modificationsValue?: number
  offRoadRecovery?: string
  email?: string
  mobileNumber?: string
  postalAddress?: string
  newListedDriver?: {
    firstName: string
    lastName: string
    dateOfBirth: string
    gender: 'MALE' | 'FEMALE'
    licenceAge: number
    typeOfLicence?: string
    numberOfClaims?: string
    recentClaims?: 'YES' | 'NO'
    offroadTraining?: 'YES' | 'NO'
    yearsTowing?: number
    towingTraining?: 'YES' | 'NO'
  }
  updateListedDriver?: {
    matchFirstName: string
    matchLastName: string
    firstName: string
    lastName: string
    dateOfBirth: string
    gender: 'MALE' | 'FEMALE'
    licenceAge: number
    typeOfLicence?: string
    numberOfClaims?: string
    recentClaims?: 'YES' | 'NO'
    offroadTraining?: 'YES' | 'NO'
    yearsTowing?: number
    towingTraining?: 'YES' | 'NO'
  }
}

/**
 * Mutates the cached dev mock for `policyRef` so PolicyView shows edits after a mocked
 * `updateRegistrationNumber` (dev mode does not call the real API).
 */
export function applyDevMockPolicyUpdate(policyRef: string, body: DevMockPolicyUpdateBody): void {
  const policy = getMockPolicy(policyRef) as Record<string, any>

  policy.registrationNumber = body.registrationNumber
  if (body.state !== undefined) policy.registrationState = body.state
  if (body.parkingAddress !== undefined) {
    policy.parkingAddress = body.parkingAddress
    policy.policyContactPostalAddress = body.parkingAddress
  }
  if (body.overnightParking !== undefined) policy.overnightParking = body.overnightParking
  if (body.modificationsValue !== undefined) policy.modificationsValue = body.modificationsValue
  if (body.offRoadRecovery !== undefined) {
    policy.offRoadRecovery =
      OFF_ROAD_DISPLAY_BY_OPTION[body.offRoadRecovery.trim()] ?? policy.offRoadRecovery
  }
  if (body.email !== undefined) {
    policy.email = body.email
    policy.policyContactEmail = body.email
  }
  if (body.mobileNumber !== undefined) policy.mobileNumber = body.mobileNumber
  if (body.postalAddress !== undefined) policy.postalAddress = body.postalAddress

  if (body.newListedDriver) {
    const d = body.newListedDriver
    const fullName = `${d.firstName} ${d.lastName}`.trim()
    const drivers: string[] = Array.isArray(policy.listedDrivers) ? [...policy.listedDrivers] : []
    if (fullName && !drivers.includes(fullName)) drivers.push(fullName)
    policy.listedDrivers = drivers

    const details = Array.isArray(policy.listedDriversDetails) ? [...policy.listedDriversDetails] : []
    details.push({
      id: `dev-${Date.now()}`,
      firstName: d.firstName,
      lastName: d.lastName,
      dateOfBirth: apiDobToDisplayDob(d.dateOfBirth),
      gender: d.gender === 'MALE' ? 'Male' : 'Female',
      licenseType: portalLicenseTypeFromEntsiaCode(d.typeOfLicence),
      licenseAge: d.licenceAge,
      hasSafetyCourse: d.offroadTraining === 'YES',
      ...(d.yearsTowing != null ? { yearsTowing: d.yearsTowing } : {}),
      ...(d.towingTraining === 'YES' || d.towingTraining === 'NO'
        ? { hasTowingCourse: d.towingTraining === 'YES' }
        : {}),
      claims: [],
    })
    policy.listedDriversDetails = details
  }

  if (body.updateListedDriver) {
    const u = body.updateListedDriver
    const want = `${u.matchFirstName} ${u.matchLastName}`.trim().toLowerCase().replace(/\s+/g, ' ')
    const matchRow = (first: string, last: string) =>
      `${first} ${last}`.trim().toLowerCase().replace(/\s+/g, ' ') === want
    if (Array.isArray(policy.listedDriversDetails)) {
      policy.listedDriversDetails = policy.listedDriversDetails.map(
        (d: { firstName?: string; lastName?: string; id?: string }) => {
          if (!matchRow(d.firstName ?? '', d.lastName ?? '')) return d
          return {
            ...d,
            firstName: u.firstName,
            lastName: u.lastName,
            dateOfBirth: apiDobToDisplayDob(u.dateOfBirth),
            gender: u.gender === 'MALE' ? 'Male' : 'Female',
            licenseType: portalLicenseTypeFromEntsiaCode(u.typeOfLicence),
            licenseAge: u.licenceAge,
            hasSafetyCourse: u.offroadTraining === 'YES',
            ...(u.yearsTowing != null ? { yearsTowing: u.yearsTowing } : {}),
            ...(u.towingTraining === 'YES' || u.towingTraining === 'NO'
              ? { hasTowingCourse: u.towingTraining === 'YES' }
              : {}),
          }
        }
      )
    }
    if (Array.isArray(policy.listedDrivers)) {
      policy.listedDrivers = policy.listedDrivers.map((name: string) => {
        const parts = String(name).trim().split(/\s+/)
        const f = parts[0] || ''
        const l = parts.slice(1).join(' ')
        if (!matchRow(f, l)) return name
        return `${u.firstName} ${u.lastName}`.trim()
      })
    }
  }
}

/**
 * Mock policies response for dev mode (minimal data for Dashboard)
 */
export const getMockPolicies = () => ({
  policies: [
    getMockPolicy('dxt472'),
    getMockPolicy('POL40000399'),
  ],
  firstName: 'Dev',
});

/** Minimal user row for GET /api/user when running without the Functions API */
export const getMockUser = (): User => ({
  userId: 'dev-user-id',
  status: 'active',
  createdAt: new Date().toISOString(),
  lastSeenAt: new Date().toISOString(),
  csrfToken: 'dev-mock-portal-csrf',
})
